/**
 * budgetConfig.js
 * Static configuration for the Budget & Targets page.
 * NEVER import from a database. NEVER add migrations for this file.
 * To add a new channel or campaign type: edit this file only.
 */

// Campaign types available per channel
export const CHANNEL_CAMPAIGN_TYPES = {
  google_ads: [
    { key: 'search_brand',   label: 'Search – Brand' },
    { key: 'search_generic', label: 'Search – Generic' },
    { key: 'search',         label: 'Search' },
    { key: 'shopping',       label: 'Shopping' },
    { key: 'pmax',           label: 'Performance Max' },
    { key: 'youtube',        label: 'YouTube' },
    { key: 'demand_gen',     label: 'Demand Gen' },
    { key: 'display',        label: 'Display' },
    { key: 'smart',          label: 'Smart' },
    { key: 'local_services', label: 'Local Services' },
    { key: 'app',            label: 'App' },
    { key: 'hotel',          label: 'Hotel' },
    { key: 'travel',         label: 'Travel' },
  ],
  meta_ads: [
    { key: 'prospecting',    label: 'Prospecting' },
    { key: 'retargeting',    label: 'Retargeting' },
    { key: 'lookalike',      label: 'Lookalike' },
  ],
  linkedin_ads: [
    { key: 'lead_gen_forms',     label: 'Lead Gen Forms' },
    { key: 'sponsored_content',  label: 'Sponsored Content' },
    { key: 'inmail',             label: 'InMail' },
  ],
  amazon_ads: [
    { key: 'sponsored_products', label: 'Sponsored Products' },
    { key: 'sponsored_brands',   label: 'Sponsored Brands' },
    { key: 'sponsored_display',  label: 'Sponsored Display' },
  ],
};

/** Display label for channel keys (e.g. google_ads → Google Ads). */
export function formatChannelName(key) {
  if (!key) return '';
  return key
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// Channel display metadata
export const CHANNEL_META = {
  google_ads:   { label: 'Google Ads',   icon: 'G', iconBg: '#E8F0FE', iconColor: '#1A73E8' },
  meta_ads:     { label: 'Meta Ads',     icon: 'M', iconBg: '#E7F3FF', iconColor: '#1877F2' },
  linkedin_ads: { label: 'LinkedIn Ads', icon: 'Li', iconBg: '#E8F4F8', iconColor: '#0A66C2' },
  amazon_ads:   { label: 'Amazon Ads',   icon: 'A', iconBg: '#FFF3E0', iconColor: '#FF9900' },
  bing_ads:     {
    label: 'Microsoft Ads',
    icon: 'MS',
    iconBg: 'var(--ad-status-blue-bg)',
    iconColor: 'var(--ad-status-blue-text)',
  },
};

/** Max channel-level KPI targets shown in KC Profile Zone 2 (per engine). */
export const MAX_KPI_TARGETS_PER_CHANNEL = 6;

/** Paid-search sister engines — per-engine tabs in KC budget/KPI panels (KB10a). */
export const SISTER_CHANNEL_PAIRS = {
  google_ads: 'bing_ads',
  bing_ads: 'google_ads',
};

/**
 * Connected engines for panel tabs: [primary] or [primary, sister] when sister is connected.
 * @param {string | null | undefined} primaryPlatform
 * @param {Array<{ platform?: string, connected_account_id?: string, is_active?: boolean }>} connectedChannels
 * @returns {string[]}
 */
export function getConnectedSisterEngines(primaryPlatform, connectedChannels) {
  if (!primaryPlatform) return [];
  const sister = SISTER_CHANNEL_PAIRS[primaryPlatform];
  const connected = new Set(
    (connectedChannels || [])
      .filter((ch) => ch.is_active !== false && ch.connected_account_id)
      .map((ch) => ch.platform)
      .filter(Boolean),
  );
  if (!sister || !connected.has(sister)) return [primaryPlatform];
  return [primaryPlatform, sister];
}

// Default threshold percentages (expert rule: ±15-20% for CPA, ±15% for ROAS)
export const DEFAULT_THRESHOLD_PCT = {
  cpa:     { minPct: 30, maxPct: 18 }, // CPA too low is also a problem
  roas:    { minPct: 20, maxPct: 0  }, // ROAS only alert when below
  cpl:     { minPct: 30, maxPct: 18 },
  cpm:     { minPct: 0,  maxPct: 20 },
  default: { minPct: 20, maxPct: 20 },
};

// CM change limit default (±10% without PM approval)
export const DEFAULT_CM_CHANGE_LIMIT_PCT = 10;

// Month state constants
/** @type {{ PAST: 'past', CURRENT: 'current', FUTURE: 'future' }} */
export const MONTH_STATE = {
  PAST:    'past',    // before current month — read only
  CURRENT: 'current', // this month — editable + shows actual spend
  FUTURE:  'future',  // future months — fully editable
};

/**
 * Get the time state of a month.
 * @param {number} year
 * @param {number} month - 1-indexed (1=Jan, 12=Dec)
 * @returns {'past'|'current'|'future'}
 */
export function getMonthState(year, month) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() + 1;
  if (year < y || (year === y && month < m)) return MONTH_STATE.PAST;
  if (year === y && month === m)             return MONTH_STATE.CURRENT;
  return MONTH_STATE.FUTURE;
}

// Month names
export const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// Quarter month ranges (0-indexed month arrays)
export const QUARTER_MONTHS = {
  Q1: [0,1,2],   // Jan Feb Mar
  Q2: [3,4,5],   // Apr May Jun
  Q3: [6,7,8],   // Jul Aug Sep
  Q4: [9,10,11], // Oct Nov Dec
};
